import { z } from 'zod'

/** Response shapes from GET /workspace/bootstrap (PRD-018 §6.2). Zod so a drift fails here, not in a component. */
/** PRD-019 §3.1 model role names; `viewer` since v0.8 (Sean 2026-09-13): read-only, scoped by grants. */
export const Role = z.enum(['cfo', 'controller', 'member', 'viewer', 'tax'])
export type Role = z.infer<typeof Role>

export const Badge = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('hot'), count: z.number().int() }),
  z.object({ kind: z.literal('warning') }),
])
export type Badge = z.infer<typeof Badge>

export const TreeFile = z.object({
  id: z.string(),
  name: z.string(),
  /** PRD-020 §3.4 (QA-20): shown as a small tag in the tree when it isn't the common case (operating is unlabeled). */
  role: z.enum(['operating', 'holding', 'management', 'other']),
  badge: Badge,
  quietCount: z.number().int(),
  qboStatus: z.enum(['none', 'ok', 'reconnect']),
  /** QA-27: `none` = paper entity (no books by design); the tree draws a different glyph and never nags to connect. */
  books: z.enum(['qbo', 'none']).default('qbo'),
  inScope: z.boolean(),
  /** QA-29: the file's agent principal when one exists (created on demand by the create route otherwise). */
  agentPrincipalId: z.string().nullable().optional(),
  /** UX review 2026-09-21 notes 6 / 8: true once a CFO has set the agent's permissions (any version); the launcher gates on it before the click. */
  agentPermissions: z.boolean().default(false),
})
export const TreeCluster = z.object({
  cluster: z.object({ id: z.string(), name: z.string(), structure: z.enum(['single', 'group']), closeTargetDay: z.number().int(), badge: Badge, quietCount: z.number().int(), /** QA-29: the client's own agent (R1 IC agent); null until one is provisioned. */ agentPrincipalId: z.string().nullable().optional() }),
  files: z.array(TreeFile),
})
export type TreeCluster = z.infer<typeof TreeCluster>
export type TreeFile = z.infer<typeof TreeFile>

export const Bootstrap = z.object({
  account: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    closeTargetDay: z.number().int(),
    stepUpWindowMinutes: z.number().int(),
    strictMode: z.boolean(),
    idleTimeoutMinutes: z.number().int(),
    /** PRD-021 §3.3: the firm's one home jurisdiction; the New Client form seeds its list from its state. */
    homeJurisdiction: z.object({ id: z.string(), country: z.string(), state: z.string(), county: z.string().nullable(), city: z.string().nullable() }).nullable().default(null),
    /** PRD-018 Q3 / ADR 0032: the firm's current open month (YYYY-MM), whether a CFO holds it, and the zone its calendar runs in (ADR 0017). */
    currentOpenPeriod: z.string(),
    openMonthHold: z.boolean(),
    timezone: z.string(),
  }),
  me: z.object({
    principalId: z.string(),
    seatId: z.string().nullable(),
    name: z.string(),
    email: z.string().nullable(),
    role: Role,
    kind: z.enum(['person', 'agent', 'operator']),
    settings: z.object({ density: z.enum(['default', 'compact']), treeCollapsed: z.boolean(), lastPeriod: z.string().nullable(), collapsedClusters: z.array(z.string()) }),
  }),
  tree: z.array(TreeCluster),
  /** PRD-016 §5 order; `threads` (v2.6.2: person-facing "Agent jobs") moved into the firm section (Skills · Method · Definitions · Agent jobs) and is CFO/Controller only, so it is not one of the person-facing FIRM rows here. Skills, Schedule and Usage stay hidden (PRD-018 §3.7). */
  firmRows: z.array(z.enum(['home', 'my_work', 'work', 'team'])),
  period: z.object({
    currentOpen: z.string(),
    range: z.object({ from: z.string(), to: z.string() }),
    default: z.string(),
    /** ADR 0032: set for a CFO after the scheduler advanced the month and this person has not yet clicked Keep (nor a CFO held it). */
    autoAdvance: z.object({ from: z.string(), to: z.string(), at: z.string() }).nullable().default(null),
  }),
  counts: z.object({
    myWork: z.number().int(),
    notificationsUnread: z.number().int(),
    /** PRD-016 §5 v2.6.2 Agent jobs row badge (field name unchanged from the pre-rename "Threads" row): jobs stopped on this person (a question or a Review card). Default 0 for an API that predates it. */
    threadsWaiting: z.number().int().default(0),
    /** PRD-029 §3.7: held emails this person may see; already folded into `myWork`, exposed for the section header. */
    heldEmails: z.number().int().optional(),
  }),
  capabilities: z.object({
    canCreateClient: z.boolean(),
    canConnectFile: z.boolean(),
    canInvite: z.boolean(),
    canSetLeadOrManager: z.boolean(),
    /** False for a viewer only (PRD-019 v0.8): no Add work item, no comment composer, nothing that posts. */
    canWriteWorkItems: z.boolean(),
    settingsSecurity: z.boolean(),
    /** PRD-021 §3.23: Settings → Import from Karbon for the CFO and Controllers (step 5b). */
    importKarbon: z.boolean(),
    viewingControl: z.boolean(),
    treeCounts: z.enum(['hot_quiet', 'hot', 'none']),
  }),
  /** PRD-025 §6.4 / PRD-026 v0.2: true only for a seat flagged `palomino_operator`; Logic authoring and the operator queue show for it, partners see Logic as Method. */
  operator: z.boolean().optional(),
  version: z.string(),
})
export type Bootstrap = z.infer<typeof Bootstrap>

export const SearchResult = z.object({ kind: z.enum(['cluster', 'file', 'item', 'person']), id: z.string(), title: z.string(), subtitle: z.string().nullable(), href: z.string() })
export type SearchResult = z.infer<typeof SearchResult>

/** Additive read projection, computed by the API from status history and live dependency edges. */
export const BlockedIndicator = z.object({
  since: z.string(),
  days: z.number().int().nonnegative(),
  stalled: z.boolean(),
  severity: z.enum(['quiet', 'aging', 'stalled']),
  label: z.string(),
  waitingOn: z.object({ id: z.string(), title: z.string(), assigneeName: z.string().nullable(), assigneePrincipalId: z.string().nullable(), dueOn: z.string().nullable(), fileName: z.string().nullable() }).nullable(),
})

export const WorkItem = z.object({
  id: z.string(),
  cluster_id: z.string(),
  file_id: z.string().nullable(),
  period_id: z.string().nullable(),
  /** The item's month as YYYY-MM (PRD-018 §3.5 period tag); null for group-level and unattached items. */
  period: z.string().nullable().optional(),
  kind: z.string(),
  /** ADR 0030 (QA-32): close | recurring | task, stored at creation; the chip on every row reads it. */
  work_class: z.enum(['close', 'recurring', 'task']).optional(),
  title: z.string(),
  status: z.enum(['open', 'in_progress', 'waiting_on_you', 'review', 'blocked', 'deferred', 'done']).or(z.string()),
  status_detail: z.string().nullable().optional(),
  assignee_principal_id: z.string().nullable(),
  assignee_name: z.string().nullable().optional(),
  assignee_kind: z.enum(['person', 'agent']).nullable().optional(),
  /** Tasks review 2026-09-20: the bound catalog skill and whether it can run on the file (see `RunReadiness` in queries.ts). */
  skill: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  run_readiness: z.enum(['ready', 'needs_definitions', 'no_skill', 'no_permissions']).nullable().optional(),
  waiting_on_principal_id: z.string().nullable().optional(),
  due_on: z.string().nullable(),
  blocked_reason: z.string().nullable().optional(),
  blocked_since: z.string().nullable().optional(),
  blocked: BlockedIndicator.nullable().optional(),
  version: z.number().int(),
  updated_at: z.string(),
})
export type WorkItem = z.infer<typeof WorkItem>

export interface TeamSeat {
  id: string
  role: Role
  job_title: string | null
  accepted_at: string | null
  deactivated_at: string | null
  created_at: string
  principal_id: string
  display_name: string
  email: string
  two_factor_enabled: boolean
  leads: number
  manages: number
  /** PRD-019 §3.4 Edit member panel (QA-24): what the seat leads, manages and holds, so a downgrade can name a holder for each and a change to Viewer can hand items over. */
  lead_client_ids: string[]
  manager_file_ids: string[]
  open_items: number
  /** Active member_file_access grants (PRD-019 §3.3a/§3.3b); the id list is what the actor may read under RLS (CFO: all; Controller: their scope). */
  views: number
  view_file_ids: string[]
  active_sessions: number
}
export interface TeamInvite { id: string, email: string, role: Role, expires_at: string, resent_count: number, created_at: string, invited_by: string }
